import type {Property} from "@/src/modules/properties/types";

import {
  FREE_INSPECTION_ALLOWED_PATH,
  isPathAllowedForFreeInspectionTier,
} from "./service-tier-routes";
import {PATH_KEYS, PUBLIC_PATHS, STATIC_ROUTES} from "../utils/constants";

function firstPathSegment(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length === 0) return "";
  return parts[0].toLowerCase();
}

type StripTarget = {listPath: string; sectionKey: string};

/**
 * Nested resource URLs that must collapse to their list route before
 * `context_property_id` changes, so TanStack Query does not refetch a
 * stale resource id against the next property.
 */
function getResourceDetailStripTarget(pathname: string): StripTarget | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2) return null;
  const first = parts[0].toLowerCase();

  if (first === "parking-enforcement") {
    return {listPath: "/parking-enforcement", sectionKey: PATH_KEYS["parking-enforcement"]};
  }
  if (first === "tasks") {
    return {listPath: "/tasks", sectionKey: "tasks"};
  }
  if (first === "permanent-parking") {
    if (parts[1] === "new") return null;
    return {listPath: "/permanent-parking", sectionKey: PATH_KEYS["permanent-parking"]};
  }
  if (first === "company-vehicles") {
    return {listPath: "/company-vehicles", sectionKey: PATH_KEYS["company-vehicles"]};
  }
  if (first === "properties") {
    return {listPath: "/properties", sectionKey: "properties"};
  }
  if (first === "dispatch-centers") {
    return {listPath: "/dispatch-centers", sectionKey: "dispatchcenters"};
  }
  if (first === "shifts") {
    return {listPath: "/shifts", sectionKey: "shifts"};
  }
  return null;
}

function isSectionGranted(property: Property, sectionKey: string): boolean {
  const section = property.sections.find((s) => s.key === sectionKey);
  return !!section && section.granted === 1;
}

function defaultFallbackPath(isAdmin: boolean): string {
  return isAdmin ? "/dashboard" : "/dars";
}

/**
 * When switching the active property, returns a path to `navigate(..., {replace:true})`
 * **before** persisting the new property, so detail views do not refetch with the next
 * property's `context_property_id` while still mounted on a stale id.
 *
 * Mirrors `useCheckNavigation` section / tier rules against the **next** property.
 */
export function getNavigationPathBeforePropertyChange(args: {
  pathname: string;
  nextProperty: Property;
  isAdmin: boolean;
  isSeniorSupervisor: boolean;
}): string | null {
  const {pathname, nextProperty, isAdmin, isSeniorSupervisor} = args;
  const first = firstPathSegment(pathname);
  if (!first) return null;

  if (PUBLIC_PATHS.includes(first as (typeof PUBLIC_PATHS)[number])) {
    return null;
  }

  const canBypassFreeInspection = isAdmin || isSeniorSupervisor;
  if (nextProperty.service_tier === "free_inspection") {
    if (canBypassFreeInspection) return null;
    if (!isPathAllowedForFreeInspectionTier(pathname)) {
      return FREE_INSPECTION_ALLOWED_PATH;
    }
    return null;
  }

  const strip = getResourceDetailStripTarget(pathname);
  if (strip) {
    if (!isSectionGranted(nextProperty, strip.sectionKey)) {
      return defaultFallbackPath(isAdmin);
    }
    const normalized = pathname.endsWith("/") ? pathname.slice(0, -1) || "/" : pathname;
    if (normalized === strip.listPath) return null;
    return strip.listPath;
  }

  if (STATIC_ROUTES.includes(first as (typeof STATIC_ROUTES)[number])) {
    return null;
  }

  const pathKey = first as keyof typeof PATH_KEYS;
  const routeKey = PATH_KEYS[pathKey];
  if (!routeKey) return null;

  const isSectionEnabled = nextProperty.sections.find((t) => t.key === routeKey && t.granted === 1);
  if (!isSectionEnabled) {
    return defaultFallbackPath(isAdmin);
  }

  return null;
}
